/**
 * RoleSwitcher — the live role-view selector in the upper-left header.
 *
 * This is the centerpiece of the permission-filter demo. It:
 *   1. Lists the demo ROLES from `GET /api/auth/personas` — role labels only, no
 *      person names or emails (a production system wouldn't expose individuals).
 *   2. Shows the CURRENT role and its `entra_groups`, so the audience sees which
 *      group claims are in effect.
 *   3. Switches role instantly via `POST /auth/assume-role` (an authenticated,
 *      password-less re-issue of the JWT with that role's groups). AuthContext is
 *      updated in place, so the same query re-runs with different, permission-
 *      filtered results WITHOUT a page reload.
 *
 * The `platform_admin` role is surfaced as "Admin (all access)" — the all-tiers
 * view you land on after sign-in.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/api';
import {
  ADMIN_ROLE_KEY,
  fetchPersonas,
  groupLabel,
  type PersonaOption,
} from '../lib/personas';

/** Display label for a role option — Admin is surfaced as "all access". */
function roleLabel(option: Pick<PersonaOption, 'key' | 'role'>): string {
  return option.key === ADMIN_ROLE_KEY ? 'Admin (all access)' : option.role;
}

export function RoleSwitcher(): ReactNode {
  const { user, assumeRole } = useAuth();

  const [roles, setRoles] = useState<PersonaOption[]>([]);
  const [open, setOpen] = useState(false);
  const [switchingKey, setSwitchingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPersonas()
      .then((list) => {
        if (cancelled) return;
        // Admin (all access) first, then the rest in roster order.
        const sorted = [...list].sort((a, b) =>
          a.key === ADMIN_ROLE_KEY ? -1 : b.key === ADMIN_ROLE_KEY ? 1 : 0,
        );
        setRoles(sorted);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load roles');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function handleSwitch(option: PersonaOption): Promise<void> {
    if (switchingKey) return;
    setError(null);
    setSwitchingKey(option.key);
    try {
      await assumeRole(option.key);
      setOpen(false);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Failed to switch role');
    } finally {
      setSwitchingKey(null);
    }
  }

  // Resolve the current role's display label (maps admin role to "all access").
  const currentOption = roles.find((r) => r.role === user?.role);
  const currentLabel = currentOption
    ? roleLabel(currentOption)
    : (user?.role ?? 'Select role');

  return (
    <div className="relative" ref={containerRef} data-testid="role-switcher">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch role view"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 rounded-lg border border-steel-200 bg-white px-3 py-1.5 text-left transition-colors hover:bg-steel-50 focus:outline-none focus:ring-2 focus:ring-navy-500"
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-900 text-white"
          aria-hidden="true"
        >
          {/* Generic role glyph — deliberately not a person's initials. */}
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-7 14a7 7 0 0 1 14 0 1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z" />
          </svg>
        </span>
        <span className="leading-tight">
          <span className="block text-[11px] uppercase tracking-wide text-steel-400">
            Viewing as
          </span>
          <span className="block text-sm font-medium text-navy-900">
            {currentLabel}
          </span>
        </span>
        <svg
          className="h-4 w-4 text-steel-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Roles"
          className="absolute left-0 z-20 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-steel-200 bg-white p-2 shadow-soft"
        >
          <p className="px-2 pt-1 pb-2 text-xs font-semibold uppercase tracking-wide text-steel-500">
            View as role (re-issues group claims)
          </p>
          {error && (
            <p role="alert" className="px-2 py-1 text-xs text-red-700">
              {error}
            </p>
          )}
          {roles.map((r) => {
            const active = user?.role === r.role;
            return (
              <button
                key={r.key}
                type="button"
                role="option"
                aria-selected={active}
                disabled={switchingKey !== null}
                onClick={() => handleSwitch(r)}
                className={`w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-steel-50 disabled:opacity-60 ${
                  active ? 'bg-steel-100' : ''
                }`}
              >
                <span className="flex items-center justify-between">
                  <span className="text-sm font-medium text-navy-900">
                    {roleLabel(r)}
                  </span>
                  {switchingKey === r.key && (
                    <span className="text-xs text-steel-500">Switching…</span>
                  )}
                </span>
                <span className="mt-1 flex flex-wrap gap-1" aria-label="Groups">
                  {r.entra_groups.map((g) => (
                    <span
                      key={g}
                      className="rounded-full bg-steel-100 px-2 py-0.5 text-[11px] text-steel-700"
                    >
                      {groupLabel(g)}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RoleSwitcher;
